import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from './SEO';
import { isCalFlow, onboardingPath, loginPath, registerPath } from '../utils/calFlow';

export default function AuthPage() {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const { sendOtp, verifyOtp, user, userRecord } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const isSignup = location.pathname === '/register' || location.pathname === '/';
  isCalFlow(location.search);

  // Redirect already-authenticated users
  useEffect(() => {
    if (user) {
      if (!userRecord?.onboarding_completed) {
        navigate(onboardingPath(location.search), { replace: true });
      } else {
        const from = location.state?.from || '/models';
        navigate(from, { replace: true });
      }
    }
  }, [user, userRecord, navigate, location]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendOtp(email, { notifySignup: isSignup });
      setStep('otp');
      setResendCooldown(60);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (newOtp.every((d) => d !== '') && value) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split('').forEach((char, i) => { newOtp[i] = char; });
    setOtp(newOtp);
    if (pasted.length === 6) {
      handleVerify(pasted);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleVerify = async (code) => {
    setError('');
    setLoading(true);
    try {
      const result = await verifyOtp(email, code);
      if (result?.needsOnboarding) {
        navigate(onboardingPath(location.search), { replace: true });
      } else {
        const from = location.state?.from || '/models';
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError('Invalid or expired code. Please try again.');
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await sendOtp(email, { notifySignup: isSignup, resent: true });
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={isSignup ? 'Start Free Trial' : 'Sign In'}
        description={
          isSignup
            ? 'Start your free 30-day trial of SharpeMetrix — full advisor access to model portfolios, Power List rankings, and quant analytics. No credit card required.'
            : 'Sign in to SharpeMetrix — institutional-grade investment intelligence for financial advisors.'
        }
        url={location.pathname === '/' ? '/' : isSignup ? '/register' : '/login'}
      />
      <div className="min-h-screen bg-bg-100 flex">
        {/* Left panel — branding */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-bg-200 flex-col justify-between p-12 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary-100 opacity-10 blur-3xl" />
            <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent-100 opacity-5 blur-3xl" />
          </div>

          <div className="relative">
            <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-8" />
          </div>

          <div className="relative space-y-10">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold text-text-100 leading-tight">
                Battle-Tested<br />Investment Strategies<br />
                <span className="text-primary-100">for Professionals.</span>
              </h1>
              <p className="mt-4 text-text-200 text-lg max-w-md">
                Quantitative model portfolios, proprietary stock rankings, and AI-powered research — built for investment advisors who demand an edge.
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  title: 'Curated Model Portfolios',
                  body: 'Browse our full library of institutional model portfolios with full signal breakdowns.',
                },
                {
                  title: 'Power List™ Stock Rankings',
                  body: 'Proprietary rankings that score equities using alternative data before the crowd reacts.',
                },
                {
                  title: 'Powerful Quantitative Analytics',
                  body: 'Factor exposures, sector attribution, and backtested signal overlays on every holding.',
                },
                {
                  title: 'AI Research Assistant',
                  body: 'Ask anything about models, tickers, or markets. Real answers, not generic responses.',
                },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3 rounded-xl bg-bg-300 bg-opacity-40 px-4 py-3">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-100 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-text-100">{f.title}</p>
                    <p className="text-xs text-text-200 mt-0.5 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="relative text-text-300 text-xs">
            © {new Date().getFullYear()} SharpeMetrix · For investment professionals only
          </p>
        </div>

        {/* Right panel — auth form */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12">
          <div className="w-full max-w-sm">
            <div className="lg:hidden mb-10 flex justify-center">
              <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-8" />
            </div>

            {step === 'email' ? (
              <div className="space-y-8">
                <div>
                  {isSignup ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-400/10 px-3 py-1 text-xs font-semibold text-green-400 ring-1 ring-inset ring-green-400/20">
                        Free 30-day trial · No credit card
                      </span>
                      <h2 className="mt-4 text-2xl font-bold text-text-100">Start your free trial</h2>
                      <p className="mt-2 text-text-200 text-sm">
                        Enter your work email to get full access to model portfolios, Power List rankings, and quant analytics.
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-text-100">Sign in to your account</h2>
                      <p className="mt-2 text-text-200 text-sm">
                        Enter your work email — we'll send you a code. No password needed.
                      </p>
                    </>
                  )}
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-text-200 mb-2">
                      Work email
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourfirm.com"
                      className="block w-full rounded-lg border-0 bg-bg-200 px-4 py-3 text-text-100 ring-1 ring-bg-300 placeholder:text-text-300 focus:ring-2 focus:ring-primary-100 text-sm transition-all"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-400 bg-red-400 bg-opacity-10 rounded-lg px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:ring-offset-2 focus:ring-offset-bg-100 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {loading ? 'Sending code...' : isSignup ? 'Start free trial' : 'Continue with email'}
                  </button>
                </form>

                {isSignup && (
                  <div className="space-y-2 text-xs text-text-200">
                    {[
                      'No credit card required',
                      'Full advisor access for 30 days',
                      'Cancel anytime — no auto-charge at trial end',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {item}
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-center text-xs text-text-300">
                  {isSignup ? (
                    <>
                      Already have an account?{' '}
                      <a href={loginPath(location.search)} className="text-primary-100 hover:underline">Sign in</a>
                      <span className="block mt-2">
                        By continuing, you agree to our{' '}
                        <a href="/terms" className="text-primary-100 hover:underline">Terms</a>
                        {' '}and{' '}
                        <a href="/privacy" className="text-primary-100 hover:underline">Privacy Policy</a>.
                      </span>
                    </>
                  ) : (
                    <>
                      Don't have an account?{' '}
                      <a href={registerPath(location.search)} className="text-primary-100 hover:underline">Start free trial</a>
                      <span className="block mt-2">
                        By continuing, you agree to our{' '}
                        <a href="/terms" className="text-primary-100 hover:underline">Terms</a>
                        {' '}and{' '}
                        <a href="/privacy" className="text-primary-100 hover:underline">Privacy Policy</a>.
                      </span>
                    </>
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-text-100">Check your email</h2>
                  <p className="mt-2 text-text-200 text-sm">
                    We sent a 6-digit code to{' '}
                    <span className="text-text-100 font-medium">{email}</span>.
                  </p>
                  <div className="mt-4 flex gap-2.5 rounded-lg bg-primary-100/10 px-3.5 py-3 ring-1 ring-inset ring-primary-100/20">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-primary-100" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm leading-relaxed text-primary-100">
                      These codes often land in spam or promotions. Check there if it isn’t in your inbox within a few seconds.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-200 mb-3">
                      Verification code
                    </label>
                    <div className="flex gap-2 justify-between" onPaste={handleOtpPaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (inputRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          className="w-12 h-14 text-center text-xl font-bold rounded-lg bg-bg-200 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 outline-none transition-all caret-transparent"
                          disabled={loading}
                        />
                      ))}
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-red-400 bg-red-400 bg-opacity-10 rounded-lg px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    onClick={() => handleVerify(otp.join(''))}
                    disabled={loading || otp.some((d) => !d)}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {loading ? 'Verifying...' : 'Verify & continue'}
                  </button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setError(''); }}
                      className="text-text-300 hover:text-text-100 transition-colors"
                    >
                      ← Change email
                    </button>
                    <button
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || loading}
                      className="text-primary-100 hover:text-primary-200 disabled:text-text-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </div>
                </div>

                <p className="text-center text-xs text-text-300">
                  Wait up to 60 seconds, then use Resend if needed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
